import React from "react";
import { Message } from "components/Atoms/Message";
import { Panel } from "../../components/panels";
import SupportQuestionMark from "../../supportbeacon/SupportQuestionMark";
import RegistrationForm from "../../components/registrationform";
import { routes } from "utilities/routing/generatedRoutes";
import PanelSegmentType from "components/panels/PanelSegment/PanelSegment";
import { LoginWithGoogle } from "components/socialloginbuttons";
import { getLcidFromParticipant } from "utilities/esigningUtils";
import { useCurrentUser, useIsLoggedIn } from "common/SessionContext";

interface Props {
  participant: server.dto.ESignParticipant
  secret: string
  suggestedUserDetails: server.dto.SuggestedUserDetails
}

const AuthenticatedDifferentName = (props: { acceptUrl: string, signOutUrl: string, participantFullName: string, participantCanSign: boolean }) => {
  const currentUser = useCurrentUser();
  const { acceptUrl: confirmUrl, signOutUrl, participantFullName, participantCanSign } = props;

  return <Panel
    title={RESX.GeneralWarnings.Warning}
    panelClassName="s1_related-feature-panel"
  >
    <div className="margin-bottom-larger">
      <Message type="error">
        {participantCanSign && <>{RESX.ESignLanding.ThisDocumentShouldBeSignedBy} <strong>{participantFullName}</strong>.</>}
        {!participantCanSign && <>{RESX.ESignLanding.ThisDocumentIsToBeReviewedBy} <strong>{participantFullName}</strong></>}
        <br />
        {RESX.ESignLanding.HoweverTheNameOnYourAccountIs} <strong>{currentUser.fullName}</strong>.
      </Message>
    </div>

    <div className="margin-bottom">
      <button className="button-new primary fluid" onClick={() => document.location.href = confirmUrl} >{RESX.ESignLanding.NeverMindIAm} {participantFullName}</button>
    </div>

    <div className="margin-bottom">
      <button className="button-new secondary fluid" onClick={() => document.location.href = signOutUrl}>{RESX.GeneralLabels.SignOut}</button>
    </div>
  </Panel >;
};

interface NotAuthenticatedProps {
  signInUrl: string
  googleSignInUrl: string
  suggestedUserDetails: server.dto.SuggestedUserDetails
  initialLanguage: server.dto.LCID
  secret: string
}

const NotAuthenticated = (props: NotAuthenticatedProps) => {
  const { signInUrl, suggestedUserDetails } = props;
  const [showRegister, setShowRegister] = React.useState(false);
  const [showHaveAccount, setShowHaveAccount] = React.useState(true);

  const createAccount = () => {
    setShowRegister(true);
    setShowHaveAccount(false);
  };

  const handleSubmit = async (email: string, firstName: string, lastName: string, password: string, username: string, language: server.dto.Culture) => {
    const formData: server.dto.AccountRegistrationInstruction = {
      email,
      firstName,
      lastName,
      password,
      username,
      locale: language.locale
    };

    return window.CoreApi.Register.SelfRegisterFromInvitation({
      account: formData,
      invitationType: "eSign",
      secret: props.secret
    }).then(() => {
      window.location.reload();
    });
  };

  return <Panel
    panelClassName="s1_related-feature-panel"
  >
    <div className="flex-column gap-large">

      <div>
        <Message type="info">
          <div className="flex">
            <div className="grow1">
              <h2>
                {RESX.ESignLanding.Hi} {props.suggestedUserDetails.name.firstName},
              </h2>
              <br />
              {RESX.ESignLanding.YouCanSaveThisInASystemOneAccountMessage}
            </div>
            <div>
              <SupportQuestionMark small articleId={serverReference.supportArticleIds.createAccountFromESign} />
            </div>
          </div>
        </Message>
      </div>

      {showHaveAccount && <div className="content-section">

        <PanelSegmentType>
          <PanelSegmentType.Header size="h2">
            <PanelSegmentType.Title title={RESX.AdvancingInvitation.DoYouHaveASystemOneAccount} />
          </PanelSegmentType.Header>
        </PanelSegmentType>

        <div className="margin-bottom">
          <button
            className="button-new primary fluid"
            onClick={createAccount}>
            {RESX.AdvancingInvitation.NoCreateAccount}
          </button>
        </div>

        <div>
          <a
            href={signInUrl}
            className="button-new secondary fluid">
            {RESX.AdvancingInvitation.YesLogin}
          </a>
        </div>
      </div>}

      {showRegister && <RegistrationForm
        onSubmit={handleSubmit}
        signInUrl={signInUrl}
        suggestedUserDetails={suggestedUserDetails}
        suggestedLanguage={props.initialLanguage}
      />}

      {showHaveAccount && <>
        <div className="text-divider info-text-medium">{RESX.GeneralLabels.Or}</div>

        <div className="flex justify-center" id="social-logins" style={{ textAlign: "center" }}>
          <LoginWithGoogle url={props.googleSignInUrl} />
        </div>
      </>}
    </div>
  </Panel >;
};

const LinkToUserPanel = (props: Props) => {
  const isLoggedIn = useIsLoggedIn();
  // No panel shown when eSign is linked to a user
  if (props.participant.linkedToUser != null) return <></>;

  const participantCanSign = props.participant.role.value !== "viewer";
  const signedByMe = props.participant.signature != null;
  const participantName = props.suggestedUserDetails.name;

  const returnUrlAfterSignIn = signedByMe ? props.participant.paths.acceptAbsolute : props.participant.paths.landingAbsolute;
  const signInUrl = `${routes.account_SignIn().absolute}?returnUrl=${encodeURIComponent(returnUrlAfterSignIn)}`;
  const signOutUrl = window.CoreApi.Authentication.Signout.absoluteUrl(encodeURIComponent(props.participant.paths.landingAbsolute));

  const initialLcid = getLcidFromParticipant(props.participant.linkedToUser, props.participant.contact);

  if (!isLoggedIn) {
    return <NotAuthenticated
      signInUrl={signInUrl}
      suggestedUserDetails={props.suggestedUserDetails}
      secret={props.secret}
      googleSignInUrl={props.participant.paths.googleSignIn}
      initialLanguage={initialLcid}
    />;
  }

  return <AuthenticatedDifferentName
    participantFullName={participantName.fullName}
    participantCanSign={participantCanSign}
    signOutUrl={signOutUrl}
    acceptUrl={props.participant.paths.acceptAbsolute}
  />;
};

export default LinkToUserPanel;